;!function(){try{var e="undefined"!=typeof globalThis?globalThis:"undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import clsx from 'clsx'
import React, { forwardRef } from 'react'

import { Link } from './link'

const styles = {
  base: [
    'relative inline-flex shrink-0 items-center justify-center gap-2 rounded-full text-sm/7 font-medium',
    'cursor-pointer transition',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mist-950/50 dark:focus-visible:outline-white/60',
    'disabled:opacity-50 aria-disabled:opacity-50',
    '*:data-[slot=icon]:-mx-0.5 *:data-[slot=icon]:size-5 sm:*:data-[slot=icon]:size-4',
  ],
  sizes: {
    md: 'px-3 py-1',
    lg: 'px-4 py-2',
  },
  solid: ['border border-transparent'],
  plain: {
    'dark/light':
      'bg-mist-950/[0.06] text-mist-950 hover:bg-mist-950/10 dark:bg-white/10 dark:text-white dark:hover:bg-white/15',
    light: 'bg-white/8 text-white hover:bg-black/10 dark:bg-white/10 dark:hover:bg-white/15',
  },
  colors: {
    // Not `bg-accent`: white on the brand blue measures 3.32:1, and this button's label is 14px,
    // where AA asks 4.5:1. It is the most-clicked element on the site and it appeared on ten routes,
    // 18 nodes, as the last contrast failure left. `--color-accent-strong` carries white at 4.76:1
    // and `--color-accent-deep` at 5.40:1, so the resting and hover states both clear it — the
    // brand blue itself is untouched everywhere else it fills something.
    primary: ['bg-accent-strong text-white hover:bg-accent-deep', 'dark:text-white'],
    'dark/zinc': [
      'bg-mist-950 text-white hover:bg-mist-800',
      'dark:bg-mist-300 dark:text-mist-950 dark:hover:bg-mist-200',
    ],
    'dark/light': [
      'bg-mist-950 text-white hover:bg-mist-800',
      'dark:bg-mist-300 dark:text-mist-950 dark:hover:bg-mist-200',
    ],
    light: ['bg-white text-mist-950 hover:bg-mist-100', 'dark:bg-mist-100 dark:text-mist-950 dark:hover:bg-white'],
  },
} as const

type ButtonColor = keyof typeof styles.colors
type PlainColor = keyof typeof styles.plain

type BaseProps = {
  className?: string
  size?: keyof typeof styles.sizes
  children: React.ReactNode
}

type SolidButtonProps = BaseProps & {
  color?: ButtonColor
  plain?: never
}

type PlainButtonProps = BaseProps & {
  color?: PlainColor
  plain: true
}

type NativeButtonProps = (SolidButtonProps | PlainButtonProps) &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>

type LinkButtonProps = (SolidButtonProps | PlainButtonProps) & {
  href: string
} & Omit<React.ComponentPropsWithoutRef<typeof Link>, 'href' | 'className' | 'children' | 'color' | 'unstyled'>

export type ButtonProps = NativeButtonProps | LinkButtonProps

function buildClasses({
  className,
  size,
  color,
  plain,
}: {
  className?: string
  size: keyof typeof styles.sizes
  color: ButtonColor | PlainColor
  plain?: boolean
}) {
  return clsx(
    className,
    styles.base,
    styles.sizes[size],
    plain ? styles.plain[color as PlainColor] : clsx(styles.solid, styles.colors[color as ButtonColor]),
  )
}

export const Button = forwardRef(function Button(
  { color, plain, size = 'lg', className, children, ...props }: ButtonProps,
  ref: React.ForwardedRef<HTMLButtonElement | HTMLAnchorElement>,
) {
  const resolvedColor = (color ?? (plain ? 'dark/light' : 'primary')) as ButtonColor | PlainColor
  const classes = buildClasses({
    className,
    size,
    color: resolvedColor,
    plain,
  })

  if ('href' in props && typeof props.href === 'string') {
    const linkProps = props as LinkButtonProps

    return (
      <Link
        {...linkProps}
        href={linkProps.href}
        unstyled
        className={classes}
        ref={ref as React.ForwardedRef<HTMLAnchorElement>}
      >
        <TouchTarget>{children}</TouchTarget>
      </Link>
    )
  }

  const buttonProps = props as Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>
  return (
    <button {...buttonProps} className={classes} ref={ref as React.ForwardedRef<HTMLButtonElement>}>
      <TouchTarget>{children}</TouchTarget>
    </button>
  )
})

type ButtonLinkProps = {
  href: string
  size?: keyof typeof styles.sizes
  color?: ButtonColor
  className?: string
  children: React.ReactNode
} & Omit<React.ComponentPropsWithoutRef<typeof Link>, 'href' | 'className' | 'children' | 'unstyled' | 'color'>

export function ButtonLink(props: ButtonLinkProps) {
  return <Button {...props} />
}

type PlainButtonBaseProps = {
  size?: keyof typeof styles.sizes
  color?: PlainColor
  className?: string
  children: React.ReactNode
}

export function PlainButton(
  props: PlainButtonBaseProps & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>,
) {
  return <Button {...props} plain />
}

export function PlainButtonLink(
  props: PlainButtonBaseProps & {
    href: string
  } & Omit<React.ComponentPropsWithoutRef<typeof Link>, 'href' | 'className' | 'children' | 'unstyled' | 'color'>,
) {
  return <Button {...props} plain />
}

export function TouchTarget({ children }: { children: React.ReactNode }) {
  return (
    <>
      <span
        className="absolute top-1/2 left-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 pointer-fine:hidden"
        aria-hidden="true"
      />
      {children}
    </>
  )
}
